import { PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { ActionModule } from '@typesApp/ActionModule'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { Customer } from '@typesApp/Session'
import { Price } from '@typesApp/Price'
import { HostEnvironment, HostSession, InitialHostEnvironment } from '@typesApp/Environment'
import { CartBundle, ProductBundle } from '@typesApp/Product'
import { RefreshCartPayload } from '@typesApp/ActionModule'
import { MyClaimsState } from '@typesApp/MyClaim'
import { ApiConfigurationError } from '@typesApp/Api'
import { AppErrorType } from '@typesApp/AppError'
import { SyncInsuranceListenerCallback, SyncInsuranceListenerList } from '@typesApp/SyncListener'
import { ConnectorErrorType, ConnectorModalPayload, ConnectorModalType } from '@typesApp/Connector'
import { AppState } from '@redux/store'
import { authApi } from '@redux/queries/auth'
import { sessionsApi } from '@redux/queries/sessions'
import { clearLoginRedirectParam } from '@utils/navigationUtil'
import { isAnonymousSession } from '@utils/appInitUtil'
import { clearLocalAuthToken, setLocalAuthToken, setLocalAuthTokenFetching } from '@utils/authToken'

type ConnectorState = {
  busy: boolean
  modal: ConnectorModalPayload
  error: ConnectorErrorType
  cartBundle?: CartBundle
  isOrderComplete: boolean
  environment: HostEnvironment
  session?: HostSession
  previousSession?: HostSession
  sessionCustomer?: Customer
  sessionExpired: boolean
  authTokenReady: boolean
  authTokenError?: boolean
  isLoggingOut: boolean
  actionModule?: ActionModule
  lastFormActionTime?: Date
  myClaims: MyClaimsState
  isProductQuoteError?: boolean
  syncInsuranceListeners?: SyncInsuranceListenerList
}

const DEFAULT_GUEST_SESSION: HostSession = {
  sessionId: '',
  isGuest: true,
  isLoggedIn: false,
  isAnonymous: false,
  isPartiallyAuthenticated: false,
}

const initialState: ConnectorState = {
  busy: false,
  modal: {
    type: ConnectorModalType.NONE,
  },
  error: ConnectorErrorType.NONE,
  isOrderComplete: false,
  sessionExpired: false,
  authTokenReady: false,
  isLoggingOut: false,
  environment: InitialHostEnvironment,
  myClaims: {
    shoppingActions: [],
  },
}

export const connectorSlice = createSlice({
  name: 'connector',
  initialState: initialState,
  reducers: {
    setConnectorBusy(state, action: PayloadAction<boolean>) {
      state.busy = action.payload
    },
    setConnectorError(state, action: PayloadAction<ConnectorErrorType>) {
      state.busy = false
      state.error = action.payload
    },
    clearConnectorError(state) {
      state.error = ConnectorErrorType.NONE
    },
    showConnectorModal(state, action: PayloadAction<ConnectorModalType | ConnectorModalPayload>) {
      if (isModalPayload(action.payload)) {
        state.modal = action.payload
      } else {
        state.modal = { type: action.payload }
      }
    },
    hideConnectorModal(state) {
      state.modal = { ...initialState.modal }
    },
    connectorAddSyncInsuranceListener(state, action: PayloadAction<SyncInsuranceListenerCallback>) {
      if (!state.syncInsuranceListeners) {
        state.syncInsuranceListeners = {
          listeners: [],
        }
      }
      state.syncInsuranceListeners?.listeners?.push(action.payload)
    },
    connectorRemoveSyncInsuranceListener(state, action: PayloadAction<SyncInsuranceListenerCallback>) {
      if (state.syncInsuranceListeners) {
        const handlers = state.syncInsuranceListeners.listeners ?? []
        const remainingHandlers = handlers.filter(handler => handler !== action.payload)
        state.syncInsuranceListeners.listeners = remainingHandlers
      }
    },
    connectorInitCartBundle(state, action: PayloadAction<CartBundle>) {
      state.isOrderComplete = false
      state.cartBundle = action.payload
    },
    connectorAfterCreateClaimRecord(state, action: PayloadAction<CartBundle | undefined>) {
      state.isOrderComplete = false
      state.cartBundle = action.payload
    },
    connectorInitAnonymousSession(state, action: PayloadAction<HostSession>) {
      const session = action.payload
      if (
        !session.sessionId &&
        !session?.cartId &&
        !session?.isGuest &&
        !session?.isLoggedIn &&
        !session?.isPartiallyAuthenticated
      ) {
        state.session = { ...action.payload, isAnonymous: true }
      }
    },
    connectorInitSession(state, action: PayloadAction<HostSession>) {
      const prevSessionId = state.previousSession?.sessionId
      const currSessionId = action.payload?.sessionId
      if ((state.isLoggingOut && !action.payload.isGuest) || (currSessionId && currSessionId === prevSessionId)) {
        return
      }

      // reset session state on new session
      const loggedInOrGuest = action.payload.isLoggedIn || action.payload.isGuest
      if (loggedInOrGuest && action.payload.sessionId !== state.session?.sessionId) {
        if (state.isLoggingOut) {
          state.isLoggingOut = false
        }
        if (state.sessionExpired) {
          state.sessionExpired = false
        }

        state.previousSession = undefined
      }

      state.session = { ...state.session, ...action.payload }

      if (loggedInOrGuest && action.payload.sessionId) {
        clearLoginRedirectParam()
      }

      const { wcToken, wcTrustedToken, ...rest } = action.payload
      console.debug('directbilling session - expired=%s loggingOut=%s', state.sessionExpired, state.isLoggingOut, rest)
    },
    connectorInitGuestSession(state, action: PayloadAction<{ userId?: string; cartId?: string; sessionId?: string }>) {
      if (!state.session) {
        state.session = {
          ...DEFAULT_GUEST_SESSION,
        }
      }

      if (action.payload) {
        const { userId, cartId, sessionId } = action.payload

        // commerce F/E may not send userId, cartId, sessionId
        // all at once so we must not overwrite current values
        if (userId) state.session.userId = userId
        if (cartId) state.session.cartId = cartId
        if (sessionId) state.session.sessionId = sessionId

        state.session.isGuest = true
        state.session.isLoggedIn = false
        state.session.isAnonymous = false
        state.session.isPartiallyAuthenticated = false

        console.debug(
          'directbilling guest session - userId=%s cartId=%s sessionId=%s',
          state.session?.userId ?? '',
          state.session?.cartId ?? '',
          state.session?.sessionId ?? ''
        )
      }
    },
    connectorInitEnvironment(state, action: PayloadAction<HostEnvironment>) {
      state.environment = action.payload
    },
    connectorInitActionModule(state, action: PayloadAction<ActionModule>) {
      state.actionModule = action.payload
    },
    connectorInitMyClaims(state, action: PayloadAction<MyClaimsState>) {
      state.myClaims = action.payload
    },
    connectorRefreshCart(state, action: PayloadAction<ClaimRecord | undefined>) {
      // payload used only by simulator
      const payload = {
        claimAmount: action.payload?.claimData?.claim?.confirmedAmount?.amount,
        residualAmount:
          (action.payload?.orderSubtotal?.amount ?? 0) -
          (action.payload?.claimData?.claim?.confirmedAmount?.amount ?? 0),
      } as RefreshCartPayload

      state.actionModule?.refreshCart(payload)
    },
    connectorLogoutInProgress(state) {
      clearLocalAuthToken()
      return {
        ...initialState,
        isLoggingOut: true,
        previousSession: {
          cartId: state.session?.cartId,
          sessionId: state.session?.sessionId,
        } as HostSession,
      }
    },
    connectorOrderComplete(state) {
      state.isOrderComplete = true
      state.cartBundle = undefined
      state.syncInsuranceListeners = undefined
    },
    connectorLastFormActionTime(state) {
      state.lastFormActionTime = new Date()
    },
    sessionExpired(state) {
      clearLocalAuthToken()
      return {
        ...initialState,
        sessionExpired: true,
      }
    },
    authTokenReady(state) {
      state.authTokenReady = true
    },
    authTokenError(state) {
      state.authTokenError = true
    },
    connectorProductQuoteError(state, action: PayloadAction<boolean>) {
      state.isProductQuoteError = action.payload
    },
    resetConnectorInitialState(state) {
      // Retain the cart bundle state as the login may be triggered from shipping page.
      // If we reset to initialState we would lose the cart bundle state.
      //
      // Also retain the session id and cart id to avoid unnecessary state updates
      // unless they have changed
      return {
        ...initialState,
        cartBundle: state.cartBundle,
        previousSession: {
          ...state.previousSession,
        } as HostSession,
      }
    },
  },
  extraReducers: builder => {
    builder.addMatcher(sessionsApi.endpoints.getSession.matchFulfilled, (state, action) => {
      state.sessionCustomer = action.payload.customer
      if (state.session) {
        state.session.userId = action.payload.customer?.id
      }
    })

    /**
     * Update session id if customer context is refetched, e.g. on partial authenticated state
     * and we still have the old session id
     */
    builder.addMatcher(sessionsApi.endpoints.getCustomerContext.matchFulfilled, (state, action) => {
      const context = action.payload
      if (state.session && context?.sessionId && state.session.sessionId !== context?.sessionId) {
        state.session.sessionId = context.sessionId
        state.session.isPartiallyAuthenticated = context.isPartiallyAuthenticated
      }
    })

    /**
     * API configuration error
     */
    builder.addMatcher(
      isAnyOf(sessionsApi.endpoints.getSession.matchRejected, sessionsApi.endpoints.getCustomerContext.matchRejected),
      (state, action) => {
        const data = action.payload?.data as ApiConfigurationError
        if (data?.errorType === AppErrorType.BAD_CONFIGURATION) {
          state.error = ConnectorErrorType.CONFIGURATION_ERROR
          console.error(data?.details)
        }
      }
    )

    /**
     * Indicate auth token is already fetching to avoid additional calls
     */
    builder.addMatcher(authApi.endpoints.getAuthToken.matchPending, (state, action) => {
      setLocalAuthTokenFetching()
    })

    builder.addMatcher(authApi.endpoints.getAuthToken.matchRejected, (state, action) => {
      // ignore rejected if another request is in progress
      if (!action.meta.condition) {
        state.authTokenReady = false
        clearLocalAuthToken()
      }
    })

    /**
     * Cache auth token on local storage
     */
    builder.addMatcher(authApi.endpoints.getAuthToken.matchFulfilled, (state, action) => {
      state.authTokenReady = true
      setLocalAuthToken(action.payload)
    })
  },
})

export const {
  setConnectorBusy,
  setConnectorError,
  clearConnectorError,
  showConnectorModal,
  hideConnectorModal,
  connectorInitCartBundle,
  connectorAfterCreateClaimRecord,
  connectorInitSession,
  connectorInitGuestSession,
  connectorInitAnonymousSession,
  connectorInitEnvironment,
  connectorInitActionModule,
  connectorInitMyClaims,
  connectorRefreshCart,
  connectorLogoutInProgress,
  connectorLastFormActionTime,
  connectorOrderComplete,
  sessionExpired,
  authTokenReady,
  authTokenError,
  connectorProductQuoteError,
  connectorAddSyncInsuranceListener,
  connectorRemoveSyncInsuranceListener,
  resetConnectorInitialState,
} = connectorSlice.actions

const isModalPayload = (payload: ConnectorModalType | ConnectorModalPayload): payload is ConnectorModalPayload =>
  payload['type']

export const connectorBusySelector = (state: AppState): boolean => state.connector.busy
export const connectorErrorSelector = (state: AppState): ConnectorErrorType => state.connector.error
export const connectorModalSelector = (state: AppState): ConnectorModalPayload => state.connector.modal
export const connectorCartProductsSelector = (state: AppState): ProductBundle[] | undefined =>
  state.connector.cartBundle?.products
export const connectorOrderShippingCostsSelector = (state: AppState): Price | undefined =>
  state.connector.cartBundle?.shippingCosts
export const connectorOrderCompleteSelector = (state: AppState): boolean => state.connector.isOrderComplete === true
export const connectorSessionSelector = (state: AppState): HostSession | undefined => state.connector.session
export const connectorCartIdSelector = (state: AppState): string | undefined => state.connector.session?.cartId
export const connectorSessionIdSelector = (state: AppState): string => state.connector.session?.sessionId ?? ''
export const connectorSessionCustomerSelector = (state: AppState): Customer | undefined =>
  state.connector.sessionCustomer
export const connectorSessionCallableSelector = (state: AppState): boolean => Boolean(state.connector.actionModule)
export const connectorEnvironmentSelector = (state: AppState): HostEnvironment => state.connector.environment
export const connectorActionModuleSelector = (state: AppState): ActionModule | undefined => state.connector.actionModule
export const connectorSyncInsuranceListenersSelector = (state: AppState): SyncInsuranceListenerList | undefined =>
  state.connector.syncInsuranceListeners
export const connectorLastFormActionTimeSelector = (state: AppState): Date | undefined =>
  state.connector.lastFormActionTime
export const connectorMyClaimsSelector = (state: AppState): MyClaimsState => state.connector.myClaims
export const connectorConfigurationErrorSelector = (state: AppState): boolean =>
  state.connector.error === ConnectorErrorType.CONFIGURATION_ERROR
export const sessionExpiredSelector = (state: AppState): boolean => state.connector.sessionExpired
export const sessionReadySelector = (state: AppState): boolean =>
  state.connector.authTokenReady && Boolean(state.connector.sessionCustomer)
export const authTokenReadySelector = (state: AppState): boolean => state.connector.authTokenReady
export const authTokenErrorSelector = (state: AppState): boolean => state.connector.authTokenError === true
export const isGuestUserSelector = (state: AppState): boolean => state.connector.session?.isGuest === true
export const isAnonymousUserSelector = (state: AppState): boolean => isAnonymousSession(state.connector.session)
export const loggedInSelector = (state: AppState): boolean => state.connector.session?.isLoggedIn === true
export const loggingOutSelector = (state: AppState): boolean => state.connector.isLoggingOut
export const partiallyAuthenticatedSelector = (state: AppState): boolean =>
  state.connector.session?.isPartiallyAuthenticated === true
export const authenticatedSelector = (state: AppState): boolean =>
  state.connector.session?.isLoggedIn === true || state.connector.session?.isPartiallyAuthenticated === true
export const connectorProductQuoteErrorSelector = (state: AppState): boolean =>
  state.connector.isProductQuoteError === true
